import React from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Paper, Tab, Tabs } from '@material-ui/core';
import DirectionsBusIcon from '@material-ui/icons/DirectionsBus';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import AssessmentIcon from '@material-ui/icons/Assessment';
import ReceiptIcon from '@material-ui/icons/Receipt';
import colors from 'config/colors';
import constants from 'config/constants';
import CommonReportsPage from './CommonReportsPage.jsx';
import FinanceReportPage from './FinanceReportPage.jsx';
import TransportReportPage from '../Transport/reports/TransportReportPage.jsx';
import UnofficialReportPage from './UnofficialReportPage.jsx';

export { selectStyle, ScrollTabPanel } from './CommonReportsPage.jsx';

const OUTER_TABS = [
  // Finance: the backend only allows super admin / admin / dean on its endpoints, so teachers don't get the tab.
  {
    key: 'finance-report',
    label: 'Finance',
    icon: <MonetizationOnIcon />,
    roles: [constants.roles.SA, constants.roles.admin],
  },
  // Same admin-only rule as the Transport page itself (teachers and super admin don't get it).
  { key: 'transport-report', label: 'Transport', icon: <DirectionsBusIcon />, roles: [constants.roles.admin] },
  // Money collected outside the official fee books, kept in its own tab so it never mixes with the Finance totals.
  // Admin only, like the Unofficial Bills page.
  { key: 'unofficial-report', label: 'Unofficial', icon: <ReceiptIcon />, roles: [constants.roles.admin] },
  { key: 'common-report', label: 'Common', icon: <AssessmentIcon /> },
];

// Reports is split into two independently-owned top-level tabs: Finance (new, this feature) and
// Common (the pre-existing School/Course-Students/Student/Employee reports flow - moved as-is,
// unchanged, into CommonReportsPage.jsx). Kept as a thin switcher rather than folding Finance into
// Common's own internal tab bar so neither flow risks touching the other's state/props wiring -
// Common can be split into its own top-level tabs later with no changes needed here.
// Query tokens that mean "show Common" - its own internal tabs (School/Course-Students/Student/
// Employee) each push one of these, so a bookmarked/shared link into one of those still lands on
// Common with that sub-tab selected. Anything else - including no query at all - defaults to
// Finance now.
const COMMON_TAB_QUERIES = [
  'common-report',
  'school-report',
  'batch-course-students-reports',
  'student-report',
  'employee-report',
];

const ReportsPage = props => {
  const role = useSelector(state => state.auth.loggedUserRole);
  const tabs = OUTER_TABS.filter(tab => !tab.roles || tab.roles.includes(role));
  const [activeKey, setActiveKey] = useState(() => {
    const search = props.location?.search || '';
    if (search.includes('transport-report')) return 'transport-report';
    if (search.includes('unofficial-report')) return 'unofficial-report';
    return COMMON_TAB_QUERIES.some(query => search.includes(query)) ? 'common-report' : 'finance-report';
  });
  // A tab this role can't see (e.g. a teacher opening a bookmarked ?finance-report or ?transport-report, or landing
  // on Reports with no query) falls back to the first tab they do have: Common for a teacher.
  const activeIndex = Math.max(
    0,
    tabs.findIndex(tab => tab.key === activeKey)
  );
  const active = tabs[activeIndex].key;

  const changeTab = (event, index) => {
    setActiveKey(tabs[index].key);
    if (props.history) {
      props.history.push(`${props.location.pathname}?${tabs[index].key}`);
    }
  };

  return (
    <div className="content">
      <Paper square style={{ marginBottom: 16 }}>
        <Tabs
          value={activeIndex}
          onChange={changeTab}
          TabIndicatorProps={{ style: { display: 'none' } }}
          textColor="inherit"
          variant="scrollable"
          scrollButtons="on">
          {tabs.map((tab, index) => (
            <Tab
              key={tab.key}
              icon={tab.icon}
              label={tab.label}
              style={{
                fontWeight: 'bold',
                color: activeIndex === index ? colors.accent : '',
                borderBottom: activeIndex === index ? `3px solid ${colors.accent}` : '',
              }}
            />
          ))}
        </Tabs>
      </Paper>
      {active === 'finance-report' && <FinanceReportPage {...props} />}
      {active === 'common-report' && <CommonReportsPage {...props} />}
      {active === 'transport-report' && <TransportReportPage />}
      {active === 'unofficial-report' && <UnofficialReportPage />}
    </div>
  );
};

export default ReportsPage;
